import { Box, Container, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { RevealOverlay } from './background/RevealOverlay';

interface SectionProps {
  id?: string;
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  children: ReactNode;
  /** Tints the band to separate it from its neighbours. */
  tone?: 'default' | 'muted' | 'inverted';
  maxWidth?: 'md' | 'lg' | 'xl';
  /** Photograph behind the band. Only for `inverted`, which carries a scrim. */
  image?: string;
}

/** One consistent vertical rhythm and heading treatment for every landing band. */
export function Section({
  id,
  eyebrow,
  title,
  subtitle,
  children,
  tone = 'default',
  maxWidth = 'lg',
  image,
}: SectionProps) {
  return (
    <Box
      component="section"
      id={id}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        py: { xs: 8, md: 12 },
        scrollMarginTop: 72,
        ...(tone === 'muted' && {
          bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.900' : 'grey.100'),
          borderBlock: 1,
          borderColor: 'divider',
        }),
        ...(tone === 'inverted' && {
          bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.900' : 'grey.800'),
          color: 'common.white',
        }),
      }}
    >
      {image && tone === 'inverted' && (
        <Box aria-hidden sx={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <Box
            component="img"
            src={image}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.5 }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(11,28,54,.86) 0%, rgba(11,28,54,.72) 100%)',
            }}
          />
        </Box>
      )}
      {image && tone === 'inverted' && <RevealOverlay radius={300} />}

      <Container maxWidth={maxWidth} sx={{ position: 'relative' }}>
        {(eyebrow || title || subtitle) && (
          <Stack spacing={1.5} sx={{ mb: { xs: 5, md: 7 }, maxWidth: 760 }}>
            {eyebrow && (
              <Typography variant="overline" sx={{ color: tone === 'inverted' ? 'grey.300' : 'text.secondary' }}>
                {eyebrow}
              </Typography>
            )}
            {title && (
              <Typography variant="h2" component="h2">
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                variant="subtitle1"
                component="p"
                sx={{ color: tone === 'inverted' ? 'grey.200' : 'text.secondary' }}
              >
                {subtitle}
              </Typography>
            )}
          </Stack>
        )}
        {children}
      </Container>
    </Box>
  );
}
